import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useListaFiltrada } from "@/lib/filtros";
import { type FiltrosAreas, filtrarAreas } from "@/lib/filtros/areas";

/**
 * Area es un área de fábrica con sus métricas de activos (GET /api/v1/areas).
 * `descripcion` es opcional (null si no se ha fijado).
 */
export type Area = {
  id: number;
  nombre: string;
  descripcion: string | null;
  num_equipos: number;
  num_procedimientos: number;
  version: number;
  updated_at: string;
};

/** AreaInput es el payload de alta/edición (POST/PUT /api/v1/areas). */
export type AreaInput = {
  nombre: string;
  descripcion: string | null;
};

/**
 * areasQueryKey namespacea la caché de áreas POR obra: `["obra", obraId, "areas"]`
 * (F7 #158). Toda lectura, invalidación y `setQueryData` de áreas cuelga de esta clave,
 * de modo que los datos de una obra jamás casan con los de otra en la caché de Query.
 */
export function areasQueryKey(obraId: number) {
  return ["obra", obraId, "areas"] as const;
}

/**
 * areasQueryOptions lista las áreas vivas con sus métricas de la obra `obraId`. Se
 * refresca invalidando la clave tras cada mutación (create/update/delete). Es el patrón
 * que replican los demás maestros de F2 (#28–#31): un queryOptions + hooks de lectura y
 * mutación, ahora con la obra en la ruta (`/obras/${obraId}/areas`) y en la clave.
 */
export function areasQueryOptions(obraId: number) {
  return queryOptions({
    queryKey: areasQueryKey(obraId),
    queryFn: () => api.get<Area[]>(`/obras/${obraId}/areas`),
  });
}

/**
 * useAreas lista las áreas de la obra, opcionalmente FILTRADAS (#247). Con red y filtros
 * activos consulta al servidor bajo la clave `[...areasQueryKey, filtros]`; sin red filtra
 * en el navegador sobre la lista base cacheada. Sin filtros es la lista base de siempre.
 */
export function useAreas(obraId: number, filtros: FiltrosAreas = {}) {
  return useListaFiltrada<Area>({
    claveBase: areasQueryKey(obraId),
    url: `/obras/${obraId}/areas`,
    filtros,
    filtrar: filtrarAreas,
  });
}

export function useCreateArea(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AreaInput) => api.post<Area>(`/obras/${obraId}/areas`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: areasQueryKey(obraId) }),
  });
}

export function useUpdateArea(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: AreaInput }) =>
      api.put<Area>(`/obras/${obraId}/areas/${id}`, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: areasQueryKey(obraId) }),
  });
}

export function useDeleteArea(obraId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<void>(`/obras/${obraId}/areas/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: areasQueryKey(obraId) }),
  });
}
